import React, { useEffect, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    InputAdornment,
    IconButton,
    Fade,
    Grid,
    Stack,
    Chip
} from "@mui/material";
import { 
    Visibility, 
    VisibilityOff, 
    EmailOutlined,
    LockOutlined,
    HotelRounded,
    ShieldOutlined,
    SpeedOutlined,
    CheckCircleOutlineRounded
} from "@mui/icons-material";

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [systemDetails, setSystemDetails] = useState({});
    const [loading, setLoading] = useState(false);

    // Defined Theme Palette
    const colors = {
        darkGreen: "#06362D",
        calPolyGreen: "#155332",
        vanilla: "#DEE2B1",
        gold: "#FFD500",
        goldenrod: "#D1A505",
        surfaceOverlay: "rgba(6, 54, 45, 0.7)"
    };

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        background: colors.calPolyGreen,
        color: colors.vanilla,
    });

    useEffect(() => {
        setSystemDetails({
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }, []);

    const login1 = async () => {
        if (!email || !password) {
            return Toast.fire({
                icon: "error",
                title: "Please enter your email and password",
            });
        }

        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            const user = auth.currentUser;

            if (user) {
                const docRef = doc(db, "authroles", user.uid);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists() && docSnap.data().role === "Reception") {
                    Toast.fire({ icon: "success", title: "Login Successful!" });

                    await addDoc(collection(db, "logindetails"), {
                        uid: user.uid,
                        username: docSnap.data().name || "Reception Desk",
                        systemDetails,
                        createdTime: Timestamp.now(),
                    });

                    window.location.href = "/home";
                } else {
                    Toast.fire({ icon: "error", title: "Access Denied: Unauthorized Role" });
                    auth.signOut();
                }
            }
        } catch (error) {
            Toast.fire({ icon: "error", title: "Invalid email or password!" });
        } finally {
            setLoading(false);
        }
    };

    const textFieldStyles = {
        mb: 2.5,
        "& .MuiOutlinedInput-root": {
            color: "#ffffff",
            backgroundColor: "rgba(222, 226, 177, 0.04)",
            borderRadius: "14px",
            transition: "all 0.25s ease-in-out",
            "& fieldset": {
                borderColor: "rgba(222, 226, 177, 0.2)",
            },
            "&:hover fieldset": {
                borderColor: "rgba(222, 226, 177, 0.45)",
            },
            "&.Mui-focused": {
                backgroundColor: "rgba(255, 213, 0, 0.05)",
                "& fieldset": {
                    borderColor: colors.gold,
                    borderWidth: "1.5px",
                },
                boxShadow: "0 0 12px rgba(255, 213, 0, 0.15)",
            },
        },
        "& .MuiInputLabel-root": {
            color: colors.vanilla,
            opacity: 0.8,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: colors.gold,
            opacity: 1,
        },
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: `radial-gradient(circle at 10% 20%, #04251f 0%, ${colors.darkGreen} 40%, #0c3323 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: { xs: 2, sm: 3, md: 5 },
                boxSizing: "border-box"
            }}
        >
            <Fade in={true} timeout={700}>
                <Paper
                    elevation={24}
                    sx={{
                        width: "100%",
                        maxWidth: 1040,
                        minHeight: 620,
                        borderRadius: "28px",
                        overflow: "hidden",
                        backgroundColor: "rgba(6, 54, 45, 0.85)",
                        backdropFilter: "blur(20px)",
                        border: "1px solid rgba(222, 226, 177, 0.15)",
                        boxShadow: "0 30px 60px -12px rgba(0, 0, 0, 0.65)",
                    }}
                >
                    <Grid container sx={{ minHeight: 620 }}>
                        {/* Left Side: Visual & Brand Showcase */}
                        <Grid
                            item
                            xs={12}
                            md={5.5}
                            sx={{
                                background: `linear-gradient(145deg, rgba(21, 83, 50, 0.9) 0%, rgba(6, 54, 45, 0.95) 100%)`,
                                p: { xs: 4, md: 5 },
                                display: { xs: "none", md: "flex" },
                                flexDirection: "column",
                                justifyContent: "space-between",
                                position: "relative",
                                borderRight: "1px solid rgba(222, 226, 177, 0.1)",
                                overflow: "hidden"
                            }}
                        >
                            {/* Subtle Ambient Decorative Circles */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    width: 250,
                                    height: 250,
                                    borderRadius: "50%",
                                    background: `radial-gradient(circle, rgba(255, 213, 0, 0.15) 0%, transparent 70%)`,
                                    top: -60,
                                    left: -60,
                                    pointerEvents: "none"
                                }}
                            />

                            {/* Top Badge */}
                            <Box sx={{ zIndex: 1 }}>
                                <Chip
                                    icon={<HotelRounded style={{ color: colors.gold, fontSize: 18 }} />}
                                    label="Front Desk Portal"
                                    sx={{
                                        backgroundColor: "rgba(255, 213, 0, 0.12)",
                                        color: colors.gold,
                                        fontWeight: 600,
                                        border: `1px solid rgba(255, 213, 0, 0.25)`,
                                        px: 1,
                                        py: 0.5
                                    }}
                                />
                            </Box>

                            {/* Center Headline & Features */}
                            <Box sx={{ zIndex: 1, my: "auto" }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 800,
                                        color: colors.vanilla,
                                        lineHeight: 1.15,
                                        mb: 2,
                                        letterSpacing: "-0.5px"
                                    }}
                                >
                                    Elevating Guest <br />
                                    <span style={{ color: colors.gold }}>Experiences</span>
                                </Typography>

                                <Typography
                                    sx={{
                                        color: colors.vanilla,
                                        opacity: 0.75,
                                        fontSize: 15,
                                        mb: 4,
                                        maxWidth: 340,
                                        lineHeight: 1.6
                                    }}
                                >
                                    Streamlined check-ins, guest management, and terminal operations tailored for HotelZio personnel.
                                </Typography>

                                <Stack spacing={2}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <CheckCircleOutlineRounded sx={{ color: colors.gold, fontSize: 20 }} />
                                        <Typography sx={{ color: colors.vanilla, fontSize: 14, fontWeight: 500 }}>
                                            Live Desk Room Allocation
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <SpeedOutlined sx={{ color: colors.gold, fontSize: 20 }} />
                                        <Typography sx={{ color: colors.vanilla, fontSize: 14, fontWeight: 500 }}>
                                            Instant Status Auditing
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <ShieldOutlined sx={{ color: colors.gold, fontSize: 20 }} />
                                        <Typography sx={{ color: colors.vanilla, fontSize: 14, fontWeight: 500 }}>
                                            Role-Secured Operational Access
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            {/* Bottom System Note */}
                            <Box sx={{ zIndex: 1 }}>
                                <Typography variant="caption" sx={{ color: colors.vanilla, opacity: 0.5 }}>
                                    Authorized Personnel Only • HotelZio v2.4
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Right Side: Authentication Form */}
                        <Grid
                            item
                            xs={12}
                            md={6.5}
                            sx={{
                                p: { xs: 3.5, sm: 5, md: 6 },
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center"
                            }}
                        >
                            <Box sx={{ width: "100%", maxWidth: 380 }}>
                                {/* Brand Header */}
                                <Box sx={{ mb: 4, textAlign: { xs: "center", md: "left" } }}>
                                    <Box
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            p: 1,
                                            borderRadius: "16px",
                                            background: "rgba(255, 213, 0, 0.08)",
                                            border: "1px solid rgba(255, 213, 0, 0.2)",
                                            mb: 2
                                        }}
                                    >
                                        <img
                                            src="/logo.png"
                                            alt="HotelZio Logo"
                                            style={{
                                                width: 44,
                                                height: 44,
                                                objectFit: "contain",
                                                filter: "drop-shadow(0px 2px 6px rgba(255, 213, 0, 0.3))"
                                            }}
                                            onError={(e) => {
                                                // Fallback if local image fails to load
                                                e.target.style.display = "none";
                                            }}
                                        />
                                    </Box>

                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 800,
                                            color: colors.gold,
                                            letterSpacing: "1px",
                                            textTransform: "uppercase",
                                            fontSize: { xs: "24px", sm: "28px" }
                                        }}
                                    >
                                        HotelZio
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: colors.vanilla, opacity: 0.75, mt: 0.5 }}
                                    >
                                        Enter your credentials to unlock terminal access
                                    </Typography>
                                </Box>

                                {/* Inputs */}
                                <Box component="form" noValidate sx={{ width: "100%" }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        label="Email Address"
                                        placeholder="reception@hotelzio.com"
                                        sx={textFieldStyles}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailOutlined sx={{ color: colors.vanilla, ml: 0.5 }} fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        label="Password"
                                        placeholder="••••••••"
                                        type={showPassword ? "text" : "password"}
                                        sx={textFieldStyles}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") login1();
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockOutlined sx={{ color: colors.vanilla, ml: 0.5 }} fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                        size="small"
                                                        sx={{ color: colors.vanilla, mr: 0.5 }}
                                                    >
                                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Box display="flex" justifyContent="flex-end" mb={3}>
                                        <Typography
                                            sx={{
                                                fontSize: "13px",
                                                color: colors.vanilla,
                                                cursor: "pointer",
                                                fontWeight: 500,
                                                opacity: 0.8,
                                                "&:hover": { color: colors.gold, opacity: 1 },
                                                transition: "all 0.2s ease"
                                            }}
                                        >
                                            Forgot Password?
                                        </Typography>
                                    </Box>

                                    {/* Submit Action */}
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={login1}
                                        disabled={loading}
                                        sx={{
                                            py: 1.6,
                                            background: colors.gold,
                                            color: colors.darkGreen,
                                            fontWeight: 800,
                                            fontSize: "15px",
                                            borderRadius: "14px",
                                            textTransform: "none",
                                            letterSpacing: "0.5px",
                                            boxShadow: `0 8px 24px rgba(255, 213, 0, 0.2)`,
                                            "&:hover": {
                                                background: colors.goldenrod,
                                                boxShadow: `0 10px 28px rgba(209, 165, 5, 0.35)`,
                                                transform: "translateY(-1px)",
                                            },
                                            transition: "all 0.25s ease",
                                            "&.Mui-disabled": {
                                                background: "rgba(255, 213, 0, 0.25)",
                                                color: "rgba(6, 54, 45, 0.4)",
                                            },
                                        }}
                                    >
                                        {loading ? (
                                            <CircularProgress size={24} sx={{ color: colors.darkGreen }} />
                                        ) : (
                                            "Sign In to Station"
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Fade>
        </Box>
    );
}

export default Login;